import { initializeApp, getApps, deleteApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { environment } from 'src/environments/environment';

const FIREBASE_PROJECT_STORAGE_KEY = 'fawtara_firebase_project_id';
/** Bump when clients must wipe Firebase IndexedDB (e.g. Firebase project migration). */
const FIREBASE_STORAGE_VERSION = '2';
const FIREBASE_STORAGE_VERSION_KEY = 'fawtara_firebase_storage_version';

let firebaseApp: FirebaseApp | undefined;
let firebaseAnalytics: Analytics | undefined;
let storagePrepared = false;
let storagePreparePromise: Promise<void> | null = null;

/** Delete Firebase IndexedDB databases (stale installations after project switch). */
async function clearFirebaseIndexedDatabases(): Promise<void> {
  if (typeof indexedDB === 'undefined') {
    return;
  }
  try {
    if (typeof indexedDB.databases === 'function') {
      const dbs = await indexedDB.databases();
      await Promise.all(
        dbs
          .filter((db) => db.name && /firebase/i.test(db.name))
          .map(
            (db) =>
              new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(db.name!);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              }),
          ),
      );
      return;
    }
  } catch {
    /* fall through to known names */
  }
  const knownNames = [
    'firebase-installations-database',
    'firebase-messaging-database',
    'firebase-heartbeat-database',
    'firebaseLocalStorageDb',
  ];
  await Promise.all(
    knownNames.map(
      (name) =>
        new Promise<void>((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        }),
    ),
  );
}

/**
 * When Firebase project changes, wipe local Installations/Messaging storage and
 * unregister service workers so `custom-sw.js` reloads with the new config.
 * Must run before `initializeApp`.
 */
export async function prepareFirebaseWebStorage(): Promise<void> {
  if (storagePrepared) {
    return;
  }
  if (storagePreparePromise) {
    return storagePreparePromise;
  }

  storagePreparePromise = (async () => {
    const projectId = environment.firebase?.projectId;
    if (!projectId || typeof window === 'undefined') {
      storagePrepared = true;
      return;
    }

    let storedProjectId: string | null = null;
    let storedVersion: string | null = null;
    try {
      storedProjectId = localStorage.getItem(FIREBASE_PROJECT_STORAGE_KEY);
      storedVersion = localStorage.getItem(FIREBASE_STORAGE_VERSION_KEY);
    } catch {
      /* ignore */
    }

    const needsReset =
      storedProjectId !== projectId || storedVersion !== FIREBASE_STORAGE_VERSION;

    if (needsReset) {
      for (const app of getApps()) {
        try {
          await deleteApp(app);
        } catch {
          /* ignore */
        }
      }
      firebaseApp = undefined;
      firebaseAnalytics = undefined;

      await clearFirebaseIndexedDatabases();

      if ('serviceWorker' in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((reg) => reg.unregister()));
        } catch {
          /* ignore */
        }
      }

      try {
        localStorage.setItem(FIREBASE_PROJECT_STORAGE_KEY, projectId);
        localStorage.setItem(FIREBASE_STORAGE_VERSION_KEY, FIREBASE_STORAGE_VERSION);
      } catch {
        /* ignore */
      }
    }

    storagePrepared = true;
  })();

  return storagePreparePromise;
}

/**
 * Call once from `main.ts` (browser only). Initializes Firebase App + Analytics when supported.
 * Run `prepareFirebaseWebStorage()` first when the app boots.
 */
export function initializeFirebaseWeb(): FirebaseApp | undefined {
  const cfg = environment.firebase;
  if (!cfg?.apiKey || typeof window === 'undefined') {
    return undefined;
  }
  if (getApps().length > 0) {
    firebaseApp = getApps()[0];
    return firebaseApp;
  }
  firebaseApp = initializeApp(cfg);
  void setupAnalytics();
  return firebaseApp;
}

async function setupAnalytics(): Promise<void> {
  try {
    if (firebaseApp && (await isSupported())) {
      firebaseAnalytics = getAnalytics(firebaseApp);
    }
  } catch {
    /* Analytics unavailable (blocked, SSR, etc.) */
  }
}

export function getFirebaseWebApp(): FirebaseApp | undefined {
  return firebaseApp ?? getApps()[0];
}

export function getFirebaseWebAnalytics(): Analytics | undefined {
  return firebaseAnalytics;
}
